import { z } from 'zod';
import { appointmentSlot } from '../planning/appointment.js';
import { newUuidV7, uuidV7Schema } from '../uuid.js';
/**
 * **L'étape d'un contrôle** (M42) — ce que le bureau voit d'un contrôle encore sur la tablette :
 * commencé, signé, puis transmis. Recette du 20/10/2026, « pas de suivi en temps réel ».
 *
 * ⚠️ **Un fait daté, pas une proposition** ([D4](../../../../docs/ARCHITECTURE.md#d4)). L'étape
 * n'écrit rien dans le contrôle ni dans la demande — ni statut, ni saisie : le bureau apprend
 * qu'un agent a commencé, pas ce qu'il a relevé.
 *
 * ⚠️ **`submitted` ne se dépose pas** : la transmission *est* le dépôt du relevé, que le serveur
 * date déjà (`Proposal.submitted_at`). Le bureau lit les trois, la tablette n'en émet que deux.
 */
export const INSPECTION_STEPS = ['started', 'signed', 'submitted'];
export const inspectionStepSchema = z.enum(INSPECTION_STEPS);
/** Les deux étapes que la tablette dépose. */
export const FIELD_INSPECTION_STEPS = ['started', 'signed'];
export const fieldInspectionStepSchema = z.enum(FIELD_INSPECTION_STEPS);
/**
 * Le dépôt d'une étape par la tablette.
 *
 * ⚠️ **L'`id` est tiré par la tablette, une fois** ([M7](../../../../docs/MODELE-DONNEES.md#m7)) :
 * c'est lui que le serveur relit pour qu'une étape reçue deux fois n'en fasse qu'une.
 *
 * ⚠️ **`occurredAt` vient de la tablette**, comme l'heure d'un avis de passage : hors réseau,
 * l'étape part plus tard, et la dater à l'arrivée afficherait au bureau une heure où rien ne
 * s'est passé.
 */
export const recordInspectionStepSchema = z.strictObject({
    id: uuidV7Schema,
    inquiryId: uuidV7Schema,
    step: fieldInspectionStepSchema,
    occurredAt: z.iso.datetime({ offset: true }),
});
/** La fabrique de l'étape : un identifiant neuf, l'instant de la tablette. */
export function newInspectionStep(inquiryId, step, occurredAt = new Date().toISOString()) {
    return { id: newUuidV7(), inquiryId, step, occurredAt };
}
/** La dernière étape d'un contrôle, telle que le bureau la lit sur une ligne. */
export const inspectionProgressSchema = z.object({
    step: inspectionStepSchema,
    at: z.iso.datetime({ offset: true }),
});
/** L'étape en toutes lettres — la colonne « État » du tableau, et l'export du tableau. */
export const INSPECTION_STEP_LABELS = {
    started: 'Commencé',
    signed: 'Signé',
    submitted: 'Transmis',
};
/**
 * « Commencé à 10 h 12 » — et le jour en plus quand ce n'est pas aujourd'hui : « Signé le 19/10
 * à 11 h 05 ». À l'heure de Paris, comme tout instant affiché (`appointmentSlot`).
 *
 * `null` d'un instant illisible : la ligne garde son statut, sans phrase inventée.
 */
export function inspectionProgressLabel(progress, now = new Date()) {
    const slot = appointmentSlot(progress.at);
    if (slot === null)
        return null;
    // Insécables : « 10 h 12 » ne se coupe pas en fin de ligne d'une colonne étroite.
    const time = slot.time.replace(':', '\u00a0h\u00a0');
    const today = slot.day === appointmentSlot(now.toISOString())?.day;
    return today
        ? `${INSPECTION_STEP_LABELS[progress.step]} à ${time}`
        : `${INSPECTION_STEP_LABELS[progress.step]} le ${slot.shortDay} à ${time}`;
}
//# sourceMappingURL=inspection-step.js.map